import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { setLanguage } from "@/i18n";

const AuthContext = createContext(null);
const IDLE_MS = 30 * 60 * 1000; // 30 minutes inactivity auto-logout

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest, object = user
  const [token, setToken] = useState(localStorage.getItem("fundio_token") || null);
  const idleTimer = useRef(null);

  useEffect(() => {
    let active = true;
    const check = async () => {
      if (!token) { if (active) setUser(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        if (active) { setUser(data); if (data?.preferred_language) setLanguage(data.preferred_language); }
      } catch (e) {
        localStorage.removeItem("fundio_token");
        if (active) { setToken(null); setUser(false); }
      }
    };
    check();
    return () => { active = false; };
  }, [token]);

  const applyToken = (t, u) => {
    localStorage.setItem("fundio_token", t);
    setToken(t);
    setUser(u);
    if (u?.preferred_language) setLanguage(u.preferred_language);
  };

  const logout = (msg) => {
    // P0-S2F: revoke the session SERVER-SIDE (kills JWT + all its media grants immediately),
    // not just locally. Send the token explicitly so it survives the localStorage clear.
    const t = localStorage.getItem("fundio_token");
    if (t) api.post("/auth/logout", null, { headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
    localStorage.removeItem("fundio_token");
    setToken(null);
    setUser(false);
    if (msg) toast.message(msg);
  };

  // Auto-logout after inactivity (security: C)
  useEffect(() => {
    if (!token || !user) return;
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => logout("Aus Sicherheitsgründen abgemeldet (30 Min. Inaktivität)."), IDLE_MS);
    };
    const events = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [token, user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const login2fa = async (tempToken, code) => {
    try {
      const { data } = await api.post("/auth/2fa/login", { temp_token: tempToken, code });
      applyToken(data.token, data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const refreshUser = async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); } catch (e) { /* ignore */ }
  };

  const logoutAll = async () => {
    try { await api.post("/auth/logout-all"); } catch (e) { /* ignore */ }
    logout("Auf allen Geräten abgemeldet.");
  };

  return (
    <AuthContext.Provider value={{ user, token, applyToken, login, login2fa, refreshUser, logout, logoutAll }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
